// Settings → Notifications (/me/settings/notices): whether this device shows
// OS notifications for the notices that reach the open screen, and below it
// what the host adds (cloud: web push and its desktops).
//
// Promise: turning it on in a browser asks for the permission first and stays
// off unless it is granted; a denied or missing permission is said in place of
// the switch. In the desktop app, mac's permission is what is said: missing,
// with a button to System Settings in place of the switch, and the switch
// back once allowed. The choice is this device's (a device setting), not the
// account's.
import { m } from "@trameria/i18n";
import { type ComponentType, useState, useSyncExternalStore } from "react";
import type { SettingsItem } from "../app/screen-part";
import {
  type DesktopNotificationStatus,
  desktopShell,
} from "../device/desktop-shell";
import { useDeviceSetting } from "../device/device-setting";
import { notificationPermission } from "../device/os-notices";
import { SettingsBox, SettingsRow } from "../settings/settings-page";
import { Switch } from "../ui/bits";
import { Button } from "../ui/button";
import { iconOf } from "../ui/icon";
import { osNoticesOn, osNoticesSetting } from "./decide";
import { NotificationQuickSetting } from "./quick";

/** Whether the desktop app may show notifications, as its main process says. */
export interface DesktopNotices {
  status(): DesktopNotificationStatus;
  onStatusChange(
    listener: (status: DesktopNotificationStatus) => void,
  ): () => void;
  openSettings(): void;
}

/** The window bridge's, when the desktop app of this version offers it. */
export function shellNotices(): DesktopNotices | null {
  const shell = desktopShell();
  const status = shell?.notificationStatus;
  const onStatusChange = shell?.onNotificationStatusChange;
  const openSettings = shell?.openNotificationSettings;
  if (!shell || !status || !onStatusChange || !openSettings) return null;
  return {
    status: () => status.call(shell),
    onStatusChange: (listener) => onStatusChange.call(shell, listener),
    openSettings: () => openSettings.call(shell),
  };
}

const allowedEverywhere: DesktopNotices = {
  status: () => "ok",
  onStatusChange: () => () => {},
  openSettings: () => {},
};

function useOsNotices(desktop: DesktopNotices | null) {
  const [setting, setSetting] = useDeviceSetting(osNoticesSetting);
  // The browser's answer; read again only after asking (it changes outside the page rarely).
  const [permission, setPermission] = useState(notificationPermission);
  const inDesktopApp = desktop !== null || desktopShell() !== null;
  const notices = desktop ?? allowedEverywhere;
  const desktopStatus = useSyncExternalStore(
    notices.onStatusChange,
    notices.status,
  );
  const allowed = inDesktopApp || permission === "granted";
  const on =
    allowed && osNoticesOn(setting, { inDesktopApp, permission: permission });

  const turn = async (next: boolean) => {
    if (!next || allowed) {
      setSetting(next);
      return;
    }
    const answer = await Notification.requestPermission();
    setPermission(answer);
    if (answer === "granted") setSetting(true);
  };

  const reason = inDesktopApp
    ? desktopStatus === "blocked"
      ? m.notices_settings_os_blocked()
      : desktopStatus === "unavailable"
        ? m.notices_settings_os_unsigned()
        : null
    : allowed
      ? null
      : permission === null
        ? m.notices_settings_unsupported()
        : permission === "denied"
          ? m.notices_settings_denied()
          : null;

  const blocked = inDesktopApp && desktopStatus === "blocked";

  return { on, reason, blocked, notices, turn };
}

export function OsNoticesQuick({
  desktop,
}: {
  desktop: DesktopNotices | null;
}) {
  const { on, reason, blocked, notices, turn } = useOsNotices(desktop);
  return (
    <NotificationQuickSetting
      checked={on && !reason}
      disabled={!!reason && !blocked}
      onChange={(next) => {
        if (blocked) notices.openSettings();
        else void turn(next);
      }}
      note={reason}
    />
  );
}

export function DesktopNotificationQuickSettings() {
  const [desktop] = useState(shellNotices);
  return <OsNoticesQuick desktop={desktop} />;
}

function OsNoticesRow({ desktop }: { desktop: DesktopNotices | null }) {
  const { on, reason, blocked, notices, turn } = useOsNotices(desktop);

  // Why it cannot be turned on goes under the row's description (a sentence
  // squeezed into the control's column breaks badly); the control's place
  // keeps a control: the way to System Settings, or the switch, off.
  return (
    <SettingsRow
      id="os"
      label={m.notices_settings_os()}
      description={
        <>
          {m.notices_settings_os_description()}
          {reason && <span className="mt-1 block text-muted">{reason}</span>}
        </>
      }
    >
      {blocked ? (
        // No event to the bridge: contextBridge cannot copy it.
        <Button onClick={() => notices.openSettings()}>
          {m.notices_settings_os_open_settings()}
        </Button>
      ) : (
        <Switch
          label={m.notices_settings_os()}
          checked={on && !reason}
          disabled={!!reason}
          onChange={(next) => void turn(next)}
        />
      )}
    </SettingsRow>
  );
}

export function noticesSettings(options: {
  deviceSwitch: boolean;
  /** Where the window bridge is missing (the desktop app's settings window). */
  desktopNotices?: DesktopNotices;
  Extra?: ComponentType;
  extraSections?: SettingsItem["sections"];
}): SettingsItem[] {
  const { deviceSwitch, Extra } = options;
  if (!deviceSwitch && !Extra) return [];
  function NoticesSettings() {
    // Once: the subscription stays the same across renders.
    const [desktop] = useState(() => options.desktopNotices ?? shellNotices());
    return (
      <>
        {deviceSwitch && (
          <SettingsBox>
            <OsNoticesRow desktop={desktop} />
          </SettingsBox>
        )}
        {Extra && <Extra />}
      </>
    );
  }
  return [
    {
      id: "notices",
      order: 35,
      // Where this device is told (the OS's notices, this desktop or browser).
      group: "app",
      label: () => m.notices_settings_title(),
      icon: iconOf("bell"),
      description: () => m.notices_settings_description(),
      sections: [
        ...(deviceSwitch
          ? [{ id: "os", label: () => m.notices_settings_os() }]
          : []),
        ...(options.extraSections ?? []),
      ],
      Component: NoticesSettings,
    },
  ];
}
